/**
 * Unified Color Palette System
 *
 * 7 accent presets x 6 color areas x 8 slots = 336 curated colors.
 * Each preset derives its palette from a base hue with area-specific offsets.
 *
 * Area semantics:
 *   Pipeline:    1=Planned, 2=Preparation, 3=InProgress, 4=Completed, 5=FollowUp, 6-8=Reserve
 *   Findings:    1=Critical(red), 2=Major, 3=Minor, 4=Observation, 5-8=Reserve
 *   Framework:   1-6=Framework colors, 7-8=Reserve
 *   Deadlines:   1=Overdue(red), 2=Soon, 3=OK(green), 4-8=Reserve
 *   AuditStatus: 1=Pending, 2=InProgress, 3=Submitted, 4=Approved, 5=Rejected, 6=Archived, 7-8=Reserve
 *   Progress:    1=Low(<40%), 2=Mid(40-80%), 3=High(>80%), 4-8=Reserve
 */

// ── Types ──────────────────────────────────────────────────────────

export type AccentPreset =
  | "sage"
  | "steel"
  | "lavender"
  | "rose"
  | "amber"
  | "teal"
  | "indigo";

export type ColorArea =
  | "pipeline"
  | "findings"
  | "framework"
  | "deadlines"
  | "auditStatus"
  | "progress";

/** 8-element tuple of hex color strings */
export type AreaPalette = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

export type WorkspacePalette = Record<ColorArea, AreaPalette>;

// ── Preset hex-to-name mapping ─────────────────────────────────────

export const ACCENT_PRESETS: Record<string, AccentPreset> = {
  "#5a8a72": "sage",
  "#5b7ea8": "steel",
  "#8b7aad": "lavender",
  "#b86e6e": "rose",
  "#c08a5a": "amber",
  "#5a9ea8": "teal",
  "#6e6da0": "indigo",
};

// Base hues for each preset (degrees)
const PRESET_HUES: Record<AccentPreset, number> = {
  sage: 150,
  steel: 215,
  lavender: 270,
  rose: 0,
  amber: 35,
  teal: 185,
  indigo: 245,
};

// ── Curated Palettes ───────────────────────────────────────────────

/**
 * Sage (base hue 150)
 * Pipeline=150, Findings=180(+red override), Framework=130, Deadlines=210(+red/green override), AuditStatus=110, Progress=160
 */
const SAGE: WorkspacePalette = {
  pipeline: [
    "#9ab8a8", // 1 Planned — muted sage-gray
    "#d4a04a", // 2 Preparation — warm amber
    "#3d8f5e", // 3 InProgress — vivid sage green
    "#2a6040", // 4 Completed — deep forest
    "#8b6aad", // 5 FollowUp — contrasting lavender
    "#a3d8b8", // 6 Reserve
    "#c5e8d4", // 7 Reserve
    "#367a50", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED (constrained)
    "#e07840", // 2 Major — orange
    "#e8a835", // 3 Minor — amber
    "#6db88a", // 4 Observation — muted sage
    "#8fc9a2", // 5 Reserve
    "#b5dbc4", // 6 Reserve
    "#d4eadc", // 7 Reserve
    "#f0c75e", // 8 Reserve
  ],
  framework: [
    "#2e7a58", // 1 — deep sage
    "#68b878", // 2 — bright mint
    "#4a6a3a", // 3 — dark olive
    "#90c868", // 4 — lime green
    "#388068", // 5 — teal-sage
    "#78a048", // 6 — chartreuse
    "#b0d4bc", // 7 Reserve
    "#c8e2d0", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED (constrained)
    "#e8a835", // 2 Soon — amber/warning
    "#38a169", // 3 OK — GREEN (constrained)
    "#68b88c", // 4 Reserve
    "#8ccaa6", // 5 Reserve
    "#b0dcc0", // 6 Reserve
    "#d0ecd8", // 7 Reserve
    "#f0c75e", // 8 Reserve
  ],
  auditStatus: [
    "#b0a058", // 1 Pending — sage-gold
    "#3a8868", // 2 InProgress — deep sage
    "#4a8898", // 3 Submitted — sage-teal
    "#48a860", // 4 Approved — vivid sage-green
    "#c05848", // 5 Rejected — warm red-sage
    "#8a9a90", // 6 Archived — grey-sage
    "#a8b8ae", // 7 Reserve
    "#c0ccc4", // 8 Reserve
  ],
  progress: [
    "#a8c4b0", // 1 Low — pale sage
    "#6aaa80", // 2 Mid — medium sage
    "#3a9060", // 3 High — vivid sage
    "#b8d4c0", // 4 Reserve
    "#c8e0d0", // 5 Reserve
    "#d8eadc", // 6 Reserve
    "#e4f0e8", // 7 Reserve
    "#4e9a6e", // 8 Reserve
  ],
};

/**
 * Steel (base hue 215)
 * Pipeline=215, Findings=245(+red override), Framework=195, Deadlines=275(+red/green override), AuditStatus=175, Progress=225
 */
const STEEL: WorkspacePalette = {
  pipeline: [
    "#8a9cb8", // 1 Planned — muted steel-gray
    "#c89040", // 2 Preparation — warm amber
    "#3870b0", // 3 InProgress — vivid steel blue
    "#1e4878", // 4 Completed — deep navy
    "#9a60a8", // 5 FollowUp — contrasting purple
    "#a0c0dc", // 6 Reserve
    "#c0d4e8", // 7 Reserve
    "#335e90", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED
    "#d06840", // 2 Major — orange
    "#e0a030", // 3 Minor — amber
    "#6090b8", // 4 Observation — muted steel
    "#80a8c8", // 5 Reserve
    "#a0c0d8", // 6 Reserve
    "#c0d4e4", // 7 Reserve
    "#e8b850", // 8 Reserve
  ],
  framework: [
    "#2a6090", // 1 — deep navy
    "#5898c8", // 2 — bright sky blue
    "#3a5878", // 3 — dark steel
    "#78b0d8", // 4 — light azure
    "#4878a0", // 5 — slate blue
    "#6090b8", // 6 — medium steel
    "#96c4d4", // 7 Reserve
    "#b0d4e0", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED
    "#e0a030", // 2 Soon — amber
    "#38a169", // 3 OK — GREEN
    "#5e98b8", // 4 Reserve
    "#80aec8", // 5 Reserve
    "#a0c4d8", // 6 Reserve
    "#c0d8e4", // 7 Reserve
    "#e8b850", // 8 Reserve
  ],
  auditStatus: [
    "#a0a0b8", // 1 Pending — muted steel-grey
    "#3870b0", // 2 InProgress — vivid steel blue
    "#4898a0", // 3 Submitted — steel-teal
    "#48a070", // 4 Approved — steel-green
    "#c05050", // 5 Rejected — warm red
    "#8898a4", // 6 Archived — grey-steel
    "#a4b0b8", // 7 Reserve
    "#bcc8d0", // 8 Reserve
  ],
  progress: [
    "#a0b8cc", // 1 Low — pale steel
    "#6898b8", // 2 Mid
    "#3878a0", // 3 High — vivid
    "#b0c8d8", // 4 Reserve
    "#c0d4e0", // 5 Reserve
    "#d0dce8", // 6 Reserve
    "#dce8f0", // 7 Reserve
    "#4880a8", // 8 Reserve
  ],
};

/**
 * Lavender (base hue 270)
 * Pipeline=270, Findings=300(+red override), Framework=250, Deadlines=330(+red/green override), AuditStatus=230, Progress=280
 */
const LAVENDER: WorkspacePalette = {
  pipeline: [
    "#a098c0", // 1 Planned — muted lavender-gray
    "#d0984a", // 2 Preparation — warm amber
    "#7050a8", // 3 InProgress — vivid purple
    "#483078", // 4 Completed — deep violet
    "#48a088", // 5 FollowUp — contrasting teal
    "#c0b0dc", // 6 Reserve
    "#d8cce8", // 7 Reserve
    "#6848a0", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED
    "#c85090", // 2 Major — magenta-tinted
    "#d898b0", // 3 Minor — soft pink-lavender
    "#9080b0", // 4 Observation — muted lavender
    "#a898c0", // 5 Reserve
    "#c0b0d0", // 6 Reserve
    "#d8cce0", // 7 Reserve
    "#e0a0c0", // 8 Reserve
  ],
  framework: [
    "#5840a0", // 1 — deep violet
    "#9878c8", // 2 — bright lilac
    "#483878", // 3 — dark plum
    "#b098d0", // 4 — light wisteria
    "#7060a8", // 5 — iris
    "#8868b8", // 6 — medium orchid
    "#b8b0d8", // 7 Reserve
    "#d0c8e4", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED
    "#d898a0", // 2 Soon — warm pink
    "#38a169", // 3 OK — GREEN
    "#9080b0", // 4 Reserve
    "#a898c0", // 5 Reserve
    "#c0b0d0", // 6 Reserve
    "#d8c8e0", // 7 Reserve
    "#e0a8b0", // 8 Reserve
  ],
  auditStatus: [
    "#b0a0c0", // 1 Pending — muted lavender-grey
    "#7858b0", // 2 InProgress — vivid purple
    "#5890a0", // 3 Submitted — lavender-teal
    "#58a870", // 4 Approved — lavender-green
    "#c04858", // 5 Rejected — rose-red
    "#908898", // 6 Archived — grey-lavender
    "#a8a0b0", // 7 Reserve
    "#c0b8c4", // 8 Reserve
  ],
  progress: [
    "#b8a8cc", // 1 Low — pale lavender
    "#9080b8", // 2 Mid
    "#7060a8", // 3 High — vivid
    "#c8b8d8", // 4 Reserve
    "#d4c8e0", // 5 Reserve
    "#e0d8e8", // 6 Reserve
    "#eae4f0", // 7 Reserve
    "#7868b0", // 8 Reserve
  ],
};

/**
 * Rose (base hue 0)
 * Pipeline=0, Findings=30(+red override), Framework=340, Deadlines=60(+red/green override), AuditStatus=320, Progress=10
 */
const ROSE: WorkspacePalette = {
  pipeline: [
    "#b89898", // 1 Planned — muted rose-gray
    "#c89040", // 2 Preparation — warm amber
    "#b84848", // 3 InProgress — vivid rose-red
    "#782838", // 4 Completed — deep burgundy
    "#4888b0", // 5 FollowUp — contrasting blue
    "#e4b8b8", // 6 Reserve
    "#ecd0d0", // 7 Reserve
    "#a84848", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED
    "#d07040", // 2 Major — orange-rose
    "#e0a050", // 3 Minor — amber
    "#c09080", // 4 Observation — muted rose
    "#d0a898", // 5 Reserve
    "#e0c0b8", // 6 Reserve
    "#ecd8d0", // 7 Reserve
    "#e8b060", // 8 Reserve
  ],
  framework: [
    "#983850", // 1 — deep raspberry
    "#d07080", // 2 — bright coral-pink
    "#7a2840", // 3 — dark burgundy
    "#e098a0", // 4 — light salmon
    "#b85068", // 5 — medium rose
    "#c06870", // 6 — dusty rose
    "#e0a8b8", // 7 Reserve
    "#e8c0cc", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED
    "#e0a050", // 2 Soon — amber
    "#38a169", // 3 OK — GREEN
    "#c89090", // 4 Reserve
    "#d8a8a8", // 5 Reserve
    "#e4c0c0", // 6 Reserve
    "#ecd4d4", // 7 Reserve
    "#e8b060", // 8 Reserve
  ],
  auditStatus: [
    "#c0a0a8", // 1 Pending — muted rose-grey
    "#b05070", // 2 InProgress — vivid rose
    "#5890a0", // 3 Submitted — rose-teal
    "#58a870", // 4 Approved — rose-green
    "#c04040", // 5 Rejected — deep red
    "#988888", // 6 Archived — grey-rose
    "#b0a0a0", // 7 Reserve
    "#c4b8b8", // 8 Reserve
  ],
  progress: [
    "#d8b0b0", // 1 Low — pale rose
    "#c88080", // 2 Mid
    "#b85858", // 3 High — vivid
    "#e0c0c0", // 4 Reserve
    "#e8d0d0", // 5 Reserve
    "#f0dce0", // 6 Reserve
    "#f4e8e8", // 7 Reserve
    "#c06868", // 8 Reserve
  ],
};

/**
 * Amber (base hue 35)
 * Pipeline=35, Findings=65(+red override), Framework=15, Deadlines=95(+red/green override), AuditStatus=355, Progress=45
 */
const AMBER: WorkspacePalette = {
  pipeline: [
    "#b8a888", // 1 Planned — muted sand-gray
    "#48889a", // 2 Preparation — contrasting teal
    "#c08020", // 3 InProgress — vivid amber
    "#784810", // 4 Completed — deep brown
    "#8a60a8", // 5 FollowUp — contrasting purple
    "#e4cca0", // 6 Reserve
    "#ecdcb8", // 7 Reserve
    "#a87228", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED
    "#d08838", // 2 Major — deep amber-orange
    "#c8b040", // 3 Minor — yellow-amber
    "#b8a070", // 4 Observation — muted amber
    "#c8b488", // 5 Reserve
    "#d8c8a0", // 6 Reserve
    "#e4d8b8", // 7 Reserve
    "#d0c050", // 8 Reserve
  ],
  framework: [
    "#a06020", // 1 — deep burnt sienna
    "#d8a050", // 2 — bright gold
    "#805018", // 3 — dark bronze
    "#e8b868", // 4 — light honey
    "#c08030", // 5 — warm copper
    "#b89040", // 6 — antique gold
    "#e8b888", // 7 Reserve
    "#eeccaa", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED
    "#d0a830", // 2 Soon — golden amber
    "#38a169", // 3 OK — GREEN
    "#c0a870", // 4 Reserve
    "#d0b888", // 5 Reserve
    "#dcc8a0", // 6 Reserve
    "#e8d8b8", // 7 Reserve
    "#d8b040", // 8 Reserve
  ],
  auditStatus: [
    "#c8a858", // 1 Pending — warm gold
    "#b08040", // 2 InProgress — deep amber
    "#5090a0", // 3 Submitted — amber-teal
    "#58a860", // 4 Approved — warm green
    "#c84840", // 5 Rejected — warm red
    "#989088", // 6 Archived — grey-amber
    "#b0a8a0", // 7 Reserve
    "#c4bcb4", // 8 Reserve
  ],
  progress: [
    "#d8c4a0", // 1 Low — pale amber
    "#c8a468", // 2 Mid
    "#b88a40", // 3 High — vivid
    "#e0d0b0", // 4 Reserve
    "#e8d8c0", // 5 Reserve
    "#f0e4d0", // 6 Reserve
    "#f4ece0", // 7 Reserve
    "#c09450", // 8 Reserve
  ],
};

/**
 * Teal (base hue 185)
 * Pipeline=185, Findings=215(+red override), Framework=165, Deadlines=245(+red/green override), AuditStatus=145, Progress=195
 */
const TEAL: WorkspacePalette = {
  pipeline: [
    "#8ab0b0", // 1 Planned — muted teal-gray
    "#c89848", // 2 Preparation — warm amber
    "#2890a0", // 3 InProgress — vivid teal
    "#18606e", // 4 Completed — deep dark teal
    "#a068a8", // 5 FollowUp — contrasting purple
    "#a0d4d8", // 6 Reserve
    "#c0e4e8", // 7 Reserve
    "#2a8090", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED
    "#d07040", // 2 Major — orange
    "#e0a830", // 3 Minor — amber
    "#5898a0", // 4 Observation — muted teal
    "#78aeb8", // 5 Reserve
    "#98c4cc", // 6 Reserve
    "#b8d8e0", // 7 Reserve
    "#e8b850", // 8 Reserve
  ],
  framework: [
    "#207068", // 1 — deep ocean teal
    "#58b8a8", // 2 — bright seafoam
    "#185850", // 3 — dark teal
    "#78c8b8", // 4 — light aqua
    "#389080", // 5 — jade
    "#50a090", // 6 — medium celadon
    "#98cec4", // 7 Reserve
    "#b0dcd4", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED
    "#e0a830", // 2 Soon — amber
    "#38a169", // 3 OK — GREEN
    "#5898a0", // 4 Reserve
    "#78b0b8", // 5 Reserve
    "#98c4cc", // 6 Reserve
    "#b8d8e0", // 7 Reserve
    "#e8b850", // 8 Reserve
  ],
  auditStatus: [
    "#90b0a8", // 1 Pending — muted teal-grey
    "#2a8888", // 2 InProgress — vivid teal
    "#4878a8", // 3 Submitted — teal-blue
    "#48a868", // 4 Approved — teal-green
    "#c85050", // 5 Rejected — warm red
    "#889898", // 6 Archived — grey-teal
    "#a0b0b0", // 7 Reserve
    "#b8c4c4", // 8 Reserve
  ],
  progress: [
    "#a0c8cc", // 1 Low — pale teal
    "#68a8b0", // 2 Mid
    "#3890a0", // 3 High — vivid
    "#b0d4d8", // 4 Reserve
    "#c0e0e0", // 5 Reserve
    "#d0e8e8", // 6 Reserve
    "#dceef0", // 7 Reserve
    "#4898a8", // 8 Reserve
  ],
};

/**
 * Indigo (base hue 245)
 * Pipeline=245, Findings=275(+red override), Framework=225, Deadlines=305(+red/green override), AuditStatus=205, Progress=255
 */
const INDIGO: WorkspacePalette = {
  pipeline: [
    "#9090b8", // 1 Planned — muted indigo-gray
    "#c89848", // 2 Preparation — warm amber
    "#4848a8", // 3 InProgress — vivid indigo
    "#2a2870", // 4 Completed — deep dark indigo
    "#38a088", // 5 FollowUp — contrasting teal
    "#a8a8d4", // 6 Reserve
    "#c8c8e4", // 7 Reserve
    "#4040a0", // 8 Reserve
  ],
  findings: [
    "#dc3545", // 1 Critical — RED
    "#b850a0", // 2 Major — purple-magenta
    "#c888b8", // 3 Minor — soft purple-pink
    "#7878a8", // 4 Observation — muted indigo
    "#9090b8", // 5 Reserve
    "#a8a8c8", // 6 Reserve
    "#c0c0d8", // 7 Reserve
    "#d0a0c8", // 8 Reserve
  ],
  framework: [
    "#384098", // 1 — deep indigo
    "#7080c8", // 2 — bright periwinkle
    "#282878", // 3 — dark navy-indigo
    "#90a0d0", // 4 — light cornflower
    "#5060a8", // 5 — royal blue
    "#6870b8", // 6 — medium iris
    "#98b0d4", // 7 Reserve
    "#b0c4e0", // 8 Reserve
  ],
  deadlines: [
    "#dc3545", // 1 Overdue — RED
    "#c888a8", // 2 Soon — warm purple-pink
    "#38a169", // 3 OK — GREEN
    "#7878a8", // 4 Reserve
    "#9090b8", // 5 Reserve
    "#a8a8c8", // 6 Reserve
    "#c0c0d8", // 7 Reserve
    "#d0a0b8", // 8 Reserve
  ],
  auditStatus: [
    "#9898c0", // 1 Pending — muted indigo-grey
    "#5060a8", // 2 InProgress — vivid indigo
    "#4890a0", // 3 Submitted — indigo-teal
    "#50a870", // 4 Approved — indigo-green
    "#c05050", // 5 Rejected — warm red
    "#888898", // 6 Archived — grey-indigo
    "#a0a0b0", // 7 Reserve
    "#b8b8c4", // 8 Reserve
  ],
  progress: [
    "#b0a8cc", // 1 Low — pale indigo
    "#8878b8", // 2 Mid
    "#6058a8", // 3 High — vivid
    "#c0b8d8", // 4 Reserve
    "#ccc8e0", // 5 Reserve
    "#d8d4e8", // 6 Reserve
    "#e4e0f0", // 7 Reserve
    "#7068b0", // 8 Reserve
  ],
};

// ── Exported palette map ───────────────────────────────────────────

export const PALETTES: Record<AccentPreset, WorkspacePalette> = {
  sage: SAGE,
  steel: STEEL,
  lavender: LAVENDER,
  rose: ROSE,
  amber: AMBER,
  teal: TEAL,
  indigo: INDIGO,
};

// ── CSS area key mapping ───────────────────────────────────────────

export const AREA_CSS_KEYS: Record<ColorArea, string> = {
  pipeline: "pipeline",
  findings: "findings",
  framework: "framework",
  deadlines: "deadlines",
  auditStatus: "audit-status",
  progress: "progress",
};

// ── CSS variable resolver ─────────────────────────────────────────

/**
 * Reads a resolved palette CSS variable value from the document root.
 * Returns the computed hex value or a fallback gray if the variable is not set.
 */
export function getPaletteColor(area: string, slot: number): string {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(`--color-palette-${area}-${slot}`)
      .trim() || "#94a3b8"
  );
}

// ── Hex-to-HSL helpers ─────────────────────────────────────────────

function hexToHue(hex: string): number {
  const c = hex.replace("#", "");
  const r = Number.parseInt(c.substring(0, 2), 16) / 255;
  const g = Number.parseInt(c.substring(2, 4), 16) / 255;
  const b = Number.parseInt(c.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  if (delta === 0) return 0;

  let hue = 0;
  if (max === r) {
    hue = ((g - b) / delta) % 6;
  } else if (max === g) {
    hue = (b - r) / delta + 2;
  } else {
    hue = (r - g) / delta + 4;
  }

  hue = Math.round(hue * 60);
  if (hue < 0) hue += 360;
  return hue;
}

function hueDistance(a: number, b: number): number {
  const diff = Math.abs(a - b);
  return Math.min(diff, 360 - diff);
}

// ── findClosestPreset ──────────────────────────────────────────────

/**
 * Maps any hex color to the nearest accent preset by hue distance.
 * Falls back to "teal" for achromatic colors (grey/white/black).
 */
export function findClosestPreset(hex: string): AccentPreset {
  // Check exact match first
  const exact = ACCENT_PRESETS[hex.toLowerCase()];
  if (exact) return exact;

  const inputHue = hexToHue(hex);

  // Check if the color is achromatic (very low saturation)
  const c = hex.replace("#", "");
  const r = Number.parseInt(c.substring(0, 2), 16);
  const g = Number.parseInt(c.substring(2, 4), 16);
  const b = Number.parseInt(c.substring(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max - min < 15) return "teal"; // nearly achromatic, use default

  let closest: AccentPreset = "teal";
  let minDist = 360;

  for (const [preset, baseHue] of Object.entries(PRESET_HUES)) {
    const dist = hueDistance(inputHue, baseHue);
    if (dist < minDist) {
      minDist = dist;
      // Cast needed: Object.entries loses key type
      closest = preset as AccentPreset;
    }
  }

  return closest;
}
